import { useMutation } from '@apollo/client';
import {
  TimeEntryAddOn,
  TimeEntryAddOnCreateInput,
  TimeEntryAddOnsQuery,
  TimeEntryAddOnsQueryVariables,
  TimeEntryAddOnUpdateInput,
} from '__generated__/graphql';
import gql from 'graphql-tag';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import { orderBy } from 'lodash';
import { DateTime } from 'luxon';

export default function useTimeEntryAddOn() {
  const client = useGraphQLClient();
  const [createTimeEntryAddOnMutation] = useMutation(CREATE_TIME_ENTRY_ADD_ON);
  const [updateTimeEntryAddOnMutation] = useMutation(UPDATE_TIME_ENTRY_ADD_ON);

  const getTimeEntryAddOns = async (timeEntryId: string) => {
    try {
      const result = await client.request<TimeEntryAddOnsQuery, TimeEntryAddOnsQueryVariables>({
        document: TIME_ENTRY_ADD_ON_QUERY,
        variables: {
          filter: { timeEntryId: { equal: timeEntryId }, deletedOn: { isNull: true } },
        },
      });

      const sortedData = orderBy(result.timeEntryAddOns, (addOn) =>
        addOn.equipment.equipmentName.toLowerCase()
      ) as TimeEntryAddOn[];

      return sortedData;
    } catch (error) {
      throw Error(`Error fetching add ons: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const createTimeEntryAddOn = (timeEntryAddOn: TimeEntryAddOnCreateInput) => {
    return createTimeEntryAddOnMutation({ variables: { timeEntryAddOn } });
  };

  const deleteTimeEntryAddOn = (id: string) => {
    const timeEntryAddOn: TimeEntryAddOnUpdateInput = {
      id,
      deletedOn: DateTime.utc().toISO(),
    };
    return updateTimeEntryAddOnMutation({ variables: { timeEntryAddOn } });
  };

  return { getTimeEntryAddOns, createTimeEntryAddOn, deleteTimeEntryAddOn };
}

export const TIME_ENTRY_ADD_ON_QUERY = gql`
  query TimeEntryAddOns($filter: TimeEntryAddOnFilter, $sort: [TimeEntryAddOnSort!]) {
    timeEntryAddOns(filter: $filter, sort: $sort) {
      id
      timeEntryId
      equipmentId
      equipment {
        id
        equipmentName
        model {
          id
          title
          make {
            id
            title
          }
        }
      }
    }
  }
`;

export const CREATE_TIME_ENTRY_ADD_ON = gql`
  mutation CreateTimeEntryAddOn($timeEntryAddOn: TimeEntryAddOnCreateInput) {
    createTimeEntryAddOn(timeEntryAddOn: $timeEntryAddOn) {
      id
      timeEntryId
      equipmentId
      createdOn
    }
  }
`;

export const UPDATE_TIME_ENTRY_ADD_ON = gql`
  mutation UpdateTimeEntryAddOn($timeEntryAddOn: TimeEntryAddOnUpdateInput) {
    updateTimeEntryAddOn(timeEntryAddOn: $timeEntryAddOn) {
      id
      deletedOn
    }
  }
`;
